import { m } from "@plannello/i18n/messages";
import type { ItemLite } from "@plannello/shared";
import { useMemo, useRef, useState, useSyncExternalStore } from "react";
import { PropertyPicker } from "@/components/domain/PropertyPicker";
import { Popover } from "@/components/ui";
import { getCatalog, useCatalog } from "@/data/CatalogProvider";
import { useProductItems } from "@/data/items";
import { useMe } from "@/lib/queries";
import { toast } from "@/lib/toast";
import { buildPickerModel, type PickerKind } from "./pickerModels";

/**
 * One anchored picker for keyboard-opened property edits (S, P, A, L, T, M on a row or card, and
 * the bulk selection bar). `openPicker` anchors it to an element; the picker reads the targets
 * live from the cache so multi-select toggles show their effect immediately.
 */
type Request = {
  kind: PickerKind;
  targets: ItemLite[];
  anchor: HTMLElement;
  onClosed?: () => void;
  finish?: () => void;
  seq: number;
};

let current: Request | null = null;
let seq = 0;
const listeners = new Set<() => void>();
const emit = () => {
  for (const l of listeners) l();
};

export function openPicker(kind: PickerKind, targets: ItemLite[], anchor: HTMLElement, onClosed?: () => void) {
  if (targets.length === 0) return;
  // Archived products are read-only: no picker opens for their items.
  if (targets.some((t) => getCatalog()?.isArchived(t.productId))) {
    toast.show({ title: m.lifecycle_readonly_toast(), tone: "danger" });
    return;
  }
  current = { kind, targets, anchor, onClosed, seq: ++seq };
  emit();
}

export function closePicker() {
  const r = current;
  current = null;
  emit();
  r?.finish?.();
  r?.onClosed?.();
}

export function isPickerOpen() {
  return current !== null;
}

export function PickerHost() {
  const req = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => current,
  );
  return req ? <OpenPicker key={req.seq} req={req} /> : null;
}

function OpenPicker({ req }: { req: Request }) {
  const catalog = useCatalog();
  const me = useMe().data;
  // Parent candidates come from every product in the targets' spaces.
  const productIds = useMemo(() => {
    const spaces = new Set(req.targets.map((t) => catalog.productById.get(t.productId)?.spaceId));
    return catalog.allProducts.filter((p) => spaces.has(p.spaceId)).map((p) => p.id);
  }, [req.targets, catalog]);
  const all = useProductItems(productIds);
  const live = req.targets.map((t) => all.byId.get(t.id) ?? t);
  const ctx = { catalog, meId: me?.id ?? null, items: all.items };

  // The session (snapshot for one undo) starts when the picker opens.
  const [initial] = useState(() => buildPickerModel(req.kind, req.targets, ctx));
  if (req.finish === undefined && initial?.finish) req.finish = initial.finish;
  const model = buildPickerModel(req.kind, live, ctx);
  // Rows and cards mark where each property sits (data-picker-anchor); fall back to the element.
  const slot = req.kind.startsWith("field:") ? "labels" : req.kind;
  const cell = req.anchor.querySelector<HTMLElement>(`[data-picker-anchor~="${slot}"]`);
  const anchor = cell && cell.getClientRects().length > 0 ? cell : req.anchor;
  const triggerRef = useRef<HTMLElement>(anchor);
  triggerRef.current = anchor;
  if (!model) return null;

  return (
    <Popover
      triggerRef={triggerRef}
      isOpen
      onOpenChange={(open) => {
        if (!open) closePicker();
      }}
      placement="bottom start"
      className="overflow-hidden p-0"
    >
      <PropertyPicker
        label={model.label}
        shortcut={model.shortcut}
        mode={model.mode}
        options={model.options}
        selected={model.selected}
        onPick={(id) => {
          model.pick?.(id);
          closePicker();
        }}
        onToggle={(id, on) => model.toggle?.(id, on)}
      />
    </Popover>
  );
}
